import type { ReactNode } from 'react';
import {
  OrderLineItemList,
  OrderLineItemContainerVariant,
  OrderLineItemImage,
  OrderLineItemAgeBadge,
} from '@vs-shop/commerce-ui/orderLineItem';
import { CartDrawerBody, CartDrawerFooter, CartDrawerList } from '@vs-shop/commerce-ui/cartDrawer';
import { EmptyState, EmptyStateText, EmptyStateVariant } from '@vs-shop/commerce-ui/emptyState';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { IconAgeRestriction } from '@vs-shop/ui/icons';
import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';
import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';
import { Option, Schema } from 'effect';
import { OrderItem as OrderItemBase } from '@vs-shop/schemas/branded/OrderSchema';
import { useIntl } from 'react-intl';

import CartDrawerItemPlaceholder from './CartDrawerItemPlaceholder';
import { CartDrawerItem } from './CartDrawerItem';
import { CartDrawerPendingItem } from './CartDrawerPendingItem';
import CartDrawerButtons from '../../../../server/page/cart/CartDrawerButtons';

import { visiblePendingCartLinesAtom } from '../../../../../effect/atom/order/cartMutations';
import { toCartMutationItem } from '../../../../../effect/bind/cartMutationItem';
import { age18 } from '../../../../../config/checkout';
import { getDefaultCurrency } from '../../../../../config/currency';
import type { CurrencyT } from '../../../../../config/currency';
import { cartDomainItemsAtom, knownCartAtom } from '../../../../../effect/bind/order';
import type { CartLine } from '../../../../../effect/bind/order';
import { useAtomValue } from '@effect/atom-react';
import { IntlMoneyFromPrice } from '../../../../server/intl/Intl';
import {
  projectCartDrawerState,
  type CartDrawerProjectedPendingLine,
} from '@vs-shop/engine/module/cart/cartDrawerProjection';

type Props = {
  isWaiting: boolean;
  readFailed: boolean;
  /** The order summary, when the backend has confirmed an order. */
  children?: ReactNode;
};

const decodeLineQuantity = Schema.decodeSync(OrderItemBase.fields.quantity);

function renderConfirmedLine(
  item: CartLine,
  currency: CurrencyT,
  pending?: CartDrawerProjectedPendingLine
) {
  const productName = Option.getOrElse(item.name, () => item.variantCode);
  const image = Option.match(item.image, {
    onNone: () => <ImageMissingPlaceholder />,
    onSome: (template) => (
      <OrderLineItemImage
        src={resolveImageUrl(template, ImageFilter.product_card_cart)}
        alt={productName}
        preload
        width={133}
        height={100}
      />
    ),
  });

  return pending === undefined ? (
    <CartDrawerItem
      key={item.id}
      mutation={toCartMutationItem(item)}
      productName={productName}
      image={image}
      ageRestricted={Option.exists(item.ageRestriction, (restriction) => restriction === age18)}
      totalPrice={Option.getOrElse(item.totalWithTaxAfterDiscount, () => 0)}
      currency={currency}
      state="confirmed"
    />
  ) : (
    <CartDrawerItem
      key={item.id}
      mutation={toCartMutationItem(item)}
      productName={productName}
      image={image}
      ageRestricted={Option.exists(item.ageRestriction, (restriction) => restriction === age18)}
      totalPrice={Option.getOrElse(item.totalWithTaxAfterDiscount, () => 0)}
      currency={currency}
      state="pending"
      pendingAddition={{
        quantity: decodeLineQuantity(pending.quantity),
        totalPrice: pending.totalPrice,
        status: pending.status,
      }}
    />
  );
}

function renderPendingLine(line: CartDrawerProjectedPendingLine, currency: CurrencyT) {
  const image =
    line.preview?.imageUrl === undefined ? (
      <ImageMissingPlaceholder />
    ) : (
      <OrderLineItemImage
        src={line.preview.imageUrl}
        alt={line.productName}
        preload
        width={133}
        height={100}
      />
    );
  const ageBadge =
    line.preview?.ageRestricted === true ? (
      <OrderLineItemAgeBadge>
        <IconAgeRestriction className="size-[25px] tab:size-[35px]" />
      </OrderLineItemAgeBadge>
    ) : null;

  return (
    <CartDrawerPendingItem
      key={`pending-${String(line.id)}`}
      productName={line.productName}
      quantity={decodeLineQuantity(line.quantity)}
      totalPrice={line.totalPrice}
      currency={currency}
      image={image}
      ageBadge={ageBadge}
      status={line.status}
    />
  );
}

/**
 * Confirmed lines appear first. Pending lines follow. Footer controls appear last.
 * The list shows an empty state when both line sets are empty.
 * It shows a placeholder during an empty cart read.
 */
export default function CartDrawerContent({ isWaiting, readFailed, children }: Props) {
  const intl = useIntl();
  const items = useAtomValue(cartDomainItemsAtom);
  const cart = useAtomValue(knownCartAtom);
  const pendingLines = useAtomValue(visiblePendingCartLinesAtom);

  const confirmedItems = Option.match(cart, {
    onNone: () => [],
    onSome: () => items,
  });
  const currency = Option.match(cart, {
    onNone: getDefaultCurrency,
    onSome: (cartView) => cartView.currency,
  });
  const confirmedItemsTotal = Option.match(cart, {
    onNone: () => 0,
    onSome: (cartView) => Option.getOrElse(cartView.itemsTotalWithTaxAfterDiscount, () => 0),
  });
  const projection = projectCartDrawerState(confirmedItems, pendingLines, confirmedItemsTotal);
  const projectedLines = projection.lines;
  const hasLines = projection.hasLines;
  const provisionalItemsTotal = Option.getOrUndefined(projection.provisionalItemsTotal);

  return (
    <CartDrawerBody>
      <CartDrawerList>
        {hasLines ? (
          <OrderLineItemList variant={OrderLineItemContainerVariant.DRAWER}>
            {projectedLines.map((line) =>
              line.kind === 'confirmed'
                ? renderConfirmedLine(line.item, currency, line.pending)
                : renderPendingLine(line.pending, currency)
            )}
          </OrderLineItemList>
        ) : readFailed ? null : isWaiting ? (
          <CartDrawerItemPlaceholder />
        ) : (
          <EmptyState variant={EmptyStateVariant.INLINE} className="py-8">
            <EmptyStateText>
              {intl.formatMessage({
                id: 'cartDrawer.cart.empty',
                defaultMessage: 'Košík je prázdný',
              })}
            </EmptyStateText>
          </EmptyState>
        )}
      </CartDrawerList>

      <CartDrawerFooter>
        {children}
        {provisionalItemsTotal !== undefined && Option.isNone(cart) && (
          <div
            role="status"
            aria-live="polite"
            data-slot="cart-drawer-provisional-items-total"
            className="flex items-center justify-between border-t border-foreground/20 pt-4"
          >
            <span>
              {intl.formatMessage({
                id: 'cartDrawer.order.provisionalItemsTotal',
                defaultMessage: 'Předběžná cena položek',
              })}
            </span>
            <IntlMoneyFromPrice value={provisionalItemsTotal} currency={currency} />
          </div>
        )}
        <CartDrawerButtons />
      </CartDrawerFooter>
    </CartDrawerBody>
  );
}
